import axios from 'axios';
import Cookies from 'js-cookie';
import { BASE_URL } from '../settings';

const axiosInstance = axios.create({
    baseURL: BASE_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json;charset=UTF-8',
    },
});

/**
 * URLs that should NOT receive Authorization header
 */
const UNAUTHENTICATED_URLS = [
    '/auth',
    '/login',
    '/refresh',
    'https://maps.googleapis.com',
    'https://www.googleapis.com',
    'https://widget.cloudinary.com',
];

const shouldSkipAuth = (url = '') =>
    UNAUTHENTICATED_URLS.some(unAuthUrl => url?.includes(unAuthUrl));

/**
 * REQUEST INTERCEPTOR
 */
axiosInstance.interceptors.request.use(
    config => {
        console.log("Interceptors on request")
        if (shouldSkipAuth(config.url)) {
            return config;
        }

        const token = Cookies.get('token');
        if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = token.startsWith('Bearer ')
                ? token
                : `Bearer ${token}`;
        }

        return config;
    },
    error => Promise.reject(error)
);

/* ------------------------------------------------------------------ */
/*   REFRESH TOKEN HANDLING                                           */
/* ------------------------------------------------------------------ */

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        error ? prom.reject(error) : prom.resolve(token);
    });
    failedQueue = [];
};

/**
 * RESPONSE INTERCEPTOR
 */
axiosInstance.interceptors.response.use(
    response => response,
    error => {
        const originalRequest = error.config || {};

        // 🔒 Never refresh for auth / public URLs
        if (shouldSkipAuth(originalRequest.url)) {
            return Promise.reject(error);
        }

        // ❌ If refresh itself fails → logout
        if (originalRequest.url?.includes('/refresh')) {
            Cookies.remove('token');
            return Promise.reject(error);
        }

        // ✅ Handle 401 ONCE per request
        if (
            error?.response?.status === 401 &&
            !originalRequest._retry
        ) {
            originalRequest._retry = true;

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({
                        resolve: token => {
                            originalRequest.headers = originalRequest.headers || {};
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                            resolve(axiosInstance(originalRequest));
                        },
                        reject,
                    });
                });
            }

            isRefreshing = true;

            return new Promise((resolve, reject) => {
                axiosInstance
                    .post('/auth/refresh') // HttpOnly refresh cookie sent automatically
                    .then(({ data }) => {
                        const newToken = data?.token;
                        if (!newToken) {
                            throw new Error('No token returned from refresh');
                        }

                        Cookies.set('token', newToken, {
                            expires: 1,
                            secure: true,
                            sameSite: 'Strict',
                            path: '/',
                        });

                        processQueue(null, newToken);

                        originalRequest.headers = originalRequest.headers || {};
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        resolve(axiosInstance(originalRequest));
                    })
                    .catch(err => {
                        processQueue(err);
                        Cookies.remove('token');
                        reject(err);
                    })
                    .finally(() => {
                        isRefreshing = false;
                    });
            });
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;
